package com.patronat.top.features.venues.presentation

import com.patronat.top.core.base.BaseViewModel
import com.patronat.top.core.presentation.AlertController
import com.patronat.top.core.presentation.NotificationType
import com.patronat.top.core.utils.UiText
import com.patronat.top.error.AppErrorCode
import com.patronat.top.dto.CreateVenueRequest
import com.patronat.top.dto.ImageType
import com.patronat.top.dto.UNASSIGNED_VENUE_OWNER_ID
import com.patronat.top.dto.UpdateVenueRequest
import com.patronat.top.dto.UserRole
import com.patronat.top.dto.VenueAiFinding
import com.patronat.top.dto.venues.VenueStatus
import com.patronat.top.features.venues.domain.usecase.CreateVenueUseCase
import com.patronat.top.features.venues.domain.usecase.GetVenueAssigneesUseCase
import com.patronat.top.features.venues.domain.usecase.GetVenueByIdUseCase
import com.patronat.top.features.venues.domain.usecase.GetVenueDraftUseCase
import com.patronat.top.features.venues.domain.usecase.GetSubmittedVenueDraftUseCase
import com.patronat.top.features.venues.domain.usecase.PatchVenueDraftUseCase
import com.patronat.top.features.venues.domain.usecase.ReassignVenueModeratorUseCase
import com.patronat.top.features.venues.domain.usecase.ReviewVenueDraftUseCase
import com.patronat.top.features.venues.domain.usecase.UpdateVenueStatusUseCase
import com.patronat.top.features.venues.domain.usecase.UpdateVenueUseCase
import com.patronat.top.features.venues.domain.usecase.command.CreateVenueCommand
import com.patronat.top.features.venues.domain.usecase.command.GetVenueAssigneesCommand
import com.patronat.top.features.venues.domain.usecase.command.GetVenueByIdCommand
import com.patronat.top.features.venues.domain.usecase.command.GetVenueDraftCommand
import com.patronat.top.features.venues.domain.usecase.command.GetSubmittedVenueDraftCommand
import com.patronat.top.features.venues.domain.usecase.command.PatchVenueDraftCommand
import com.patronat.top.features.venues.domain.usecase.command.ReassignVenueModeratorCommand
import com.patronat.top.features.venues.domain.usecase.command.ReviewVenueDraftCommand
import com.patronat.top.features.venues.domain.usecase.command.UpdateVenueStatusCommand
import com.patronat.top.features.venues.domain.usecase.command.UpdateVenueCommand
import com.patronat.top.features.usermanagement.domain.usecase.GetUserByIdCommand
import com.patronat.top.features.usermanagement.domain.usecase.GetUserByIdUseCase
import com.patronat.top.features.venues.presentation.mappers.VenueCreateRequestBuildResult
import com.patronat.top.features.venues.presentation.mappers.VenuePatchRequestBuildResult
import com.patronat.top.features.venues.presentation.mappers.VenueUpdateRequestBuildResult
import com.patronat.top.features.venues.presentation.mappers.applyDraftPayload
import com.patronat.top.features.venues.presentation.mappers.toCreateVenueRequest
import com.patronat.top.features.venues.presentation.mappers.toPatchVenueRequest
import com.patronat.top.features.venues.presentation.mappers.toUpdateVenueRequest
import com.patronat.top.features.venues.presentation.mappers.toVenueFormState
import com.patronat.top.library.media.domain.command.UploadImageCommand
import com.patronat.top.library.media.domain.usecase.UploadImageUseCase
import com.patronat.top.ui.models.OwnerInfoData
import com.patronat.top.ui.models.VenueGalleryPhoto
import com.patronat.top.ui.models.VenueLocationData
import com.patronat.top.ui.models.VenueMainInfoData
import com.patronat.top.ui.models.VenueServiceItemData
import com.patronat.top.ui.models.WorkScheduleDay
import top.features.venues.generated.resources.Res
import top.features.venues.generated.resources.venue_error_create_failed
import top.features.venues.generated.resources.venue_error_load_assignees_failed
import top.features.venues.generated.resources.venue_error_load_failed
import top.features.venues.generated.resources.venue_error_media_upload_failed
import top.features.venues.generated.resources.venue_error_pdf_only
import top.features.venues.generated.resources.venue_error_reassign_failed
import top.features.venues.generated.resources.venue_error_upload_in_progress
import top.features.venues.generated.resources.venue_error_update_failed
import top.features.venues.generated.resources.venue_success_create_message
import top.features.venues.generated.resources.venue_success_create_title
import top.features.venues.generated.resources.venue_success_update_message
import top.features.venues.generated.resources.venue_success_update_title
import top.design_system.generated.resources.Res as DesignRes
import top.design_system.generated.resources.error_alert_title

class VenueFormViewModel(
    private val alertController: AlertController,
    private val createVenueUseCase: CreateVenueUseCase,
    private val getVenueByIdUseCase: GetVenueByIdUseCase,
    private val getVenueDraftUseCase: GetVenueDraftUseCase,
    private val getSubmittedVenueDraftUseCase: GetSubmittedVenueDraftUseCase,
    private val getVenueAssigneesUseCase: GetVenueAssigneesUseCase,
    private val getUserByIdUseCase: GetUserByIdUseCase,
    private val reassignVenueModeratorUseCase: ReassignVenueModeratorUseCase,
    private val updateVenueUseCase: UpdateVenueUseCase,
    private val patchVenueDraftUseCase: PatchVenueDraftUseCase,
    private val reviewVenueDraftUseCase: ReviewVenueDraftUseCase,
    private val updateVenueStatusUseCase: UpdateVenueStatusUseCase,
    private val uploadImageUseCase: UploadImageUseCase
) : BaseViewModel<VenueFormState, VenueFormEvent, VenueFormEffect>(
    initialState = VenueFormState()
) {

    private var lastScheduleLabels: VenueScheduleDayLabels? = null
    private var loadedVenueResponse: com.patronat.top.dto.VenueResponse? = null

    override fun onEvent(event: VenueFormEvent) {
        when (event) {
            is VenueFormEvent.Initialize -> initialize(event.mode, event.scheduleDayLabels, event.currentUserRole)
            is VenueFormEvent.OnSelectedTabChanged -> updateState { it.copy(selectedTab = event.tab) }
            is VenueFormEvent.OnOwnerInfoChanged -> updateState { it.copy(ownerInfo = event.data) }
            is VenueFormEvent.OnMainInfoChanged -> updateState { it.copy(mainInfo = event.data) }
            is VenueFormEvent.OnScheduleChanged -> updateState { it.copy(scheduleDays = event.days) }
            is VenueFormEvent.OnLocationDataChanged -> updateState { it.copy(locationData = event.data) }
            is VenueFormEvent.OnGalleryPhotosChanged -> updateState { it.copy(galleryPhotos = event.photos) }
            is VenueFormEvent.OnServicesChanged -> updateState { it.copy(services = event.services) }
            is VenueFormEvent.OnServicesFileSelected -> {
                updateState { it.copy(attachedServicesFileName = event.fileName) }
            }

            VenueFormEvent.OnGalleryAddClicked -> sendEffect(VenueFormEffect.OpenGalleryImagePicker)
            is VenueFormEvent.OnGalleryPhotoPicked -> {
                updateState { state -> state.copy(galleryPhotos = state.galleryPhotos + event.photo) }
            }
            is VenueFormEvent.OnGalleryFilePicked -> uploadGalleryFile(event.file)

            VenueFormEvent.OnUploadServicesFileClicked -> {
                sendEffect(VenueFormEffect.OpenServicesPdfPicker)
            }
            is VenueFormEvent.OnServicesFilePicked -> uploadServicesPdf(event.file)

            VenueFormEvent.OnServicesFileRemoveClicked -> {
                updateState {
                    it.copy(
                        attachedServicesFileName = null,
                        servicesPriceListMediaId = null,
                        servicesPriceListUrl = null
                    )
                }
            }

            VenueFormEvent.OnAddServiceClicked -> addService()
            VenueFormEvent.OnSaveServicesDraftClicked -> saveVenueDraft()
            VenueFormEvent.OnSubmitServicesClicked -> submitVenue()
            VenueFormEvent.OnVenueCancelClicked -> sendEffect(VenueFormEffect.NavigateBack)
            VenueFormEvent.OnVenueSubmitClicked -> submitVenue()
            VenueFormEvent.OnVenueRejectClicked -> rejectSubmittedVenueForModerator()
            VenueFormEvent.OnModeratorSelectorClicked -> toggleModeratorMenu()
            VenueFormEvent.OnDismissModeratorMenu -> {
                updateState { it.copy(isModeratorMenuExpanded = false) }
            }
            is VenueFormEvent.OnModeratorSelected -> reassignModerator(event.moderatorId)
        }
    }

    private fun initialize(mode: VenueFormMode, labels: VenueScheduleDayLabels, currentUserRole: UserRole?) {
        val previousLabels = lastScheduleLabels
        lastScheduleLabels = labels
        val shouldReinitialize = !currentState.isInitialized || currentState.mode != mode
        if (!shouldReinitialize) {
            if (previousLabels != null && previousLabels != labels) {
                updateState { state ->
                    state.copy(
                        scheduleDays = relabelScheduleDays(state.scheduleDays, labels),
                        currentUserRole = currentUserRole
                    )
                }
            } else {
                updateState { state -> state.copy(currentUserRole = currentUserRole) }
            }
            maybeLoadOwnerInfo(currentState.ownerId)
            if (currentState.mode is VenueFormMode.Edit && currentUserRole.isModeratorOrSuperAdmin()) {
                loadModeratorAssignees()
            }
            return
        }

        updateState {
            it.copy(
                isInitialized = true,
                mode = mode,
                currentUserRole = currentUserRole,
                selectedTab = VenueFormTab.Venue,
                venueStatus = null,
                draftState = null,
                scheduleDays = buildDefaultSchedule(labels),
                ownerId = null,
                venueModeratorId = null,
                moderatorAssignees = emptyList(),
                ownerReadOnly = true,
                ownerInfo = OwnerInfoData(),
                mainInfo = VenueMainInfoData(),
                locationData = VenueLocationData(),
                galleryPhotos = emptyList(),
                services = emptyList(),
                aiFindings = emptyList(),
                aiFlaggedFieldPaths = emptySet(),
                aiServiceErrorIds = emptySet(),
                aiGalleryErrorIds = emptySet(),
                attachedServicesFileName = null,
                servicesPriceListMediaId = null,
                servicesPriceListUrl = null,
                nextServiceIndex = 1,
                isLoading = false,
                isModeratorAssigneesLoading = false,
                isModeratorReassigning = false,
                isModeratorMenuExpanded = false
            )
        }
        loadedVenueResponse = null

        if (mode is VenueFormMode.CreateFromAi) {
            updateState { state ->
                mode.draft.toVenueFormState(
                    currentState = state,
                    mode = mode,
                    labels = labels
                )
            }
        }

        if (mode is VenueFormMode.Edit && currentUserRole.isModeratorOrSuperAdmin()) {
            loadModeratorAssignees()
        }

        if (mode is VenueFormMode.Edit) {
            loadVenue(mode.venueId, mode)
        }
    }

    private fun submitVenue() {
        if (currentState.isSubmitting) return
        if (currentState.isGalleryUploading || currentState.isServicesFileUploading) {
            handleError(UiText.StringResource(Res.string.venue_error_upload_in_progress))
            return
        }

        when (currentState.mode) {
            VenueFormMode.Create -> {
                createVenueFromState(submitForModeration = true)
            }
            is VenueFormMode.CreateFromAi -> {
                createVenueFromState(submitForModeration = true)
            }

            is VenueFormMode.Edit -> {
                val editMode = currentState.mode as VenueFormMode.Edit
                if (currentState.currentUserRole == UserRole.BUSINESS_OWNER) {
                    submitVenueDraft(submitForModeration = true)
                    return
                }

                if (currentState.currentUserRole.isModeratorOrSuperAdmin()) {
                    submitVenueForModerator(editMode.venueId)
                    return
                }

                updateVenue(
                    venueId = editMode.venueId,
                    request = currentState.toUpdateVenueRequest(),
                    approveAfterUpdate = false
                )
            }
        }
    }

    private fun saveVenueDraft() {
        when (currentState.mode) {
            VenueFormMode.Create -> {
                createVenueFromState(submitForModeration = false)
            }
            is VenueFormMode.CreateFromAi -> {
                createVenueFromState(submitForModeration = false)
            }

            is VenueFormMode.Edit -> {
                if (currentState.currentUserRole == UserRole.BUSINESS_OWNER) {
                    submitVenueDraft(submitForModeration = false)
                } else if (
                    currentState.currentUserRole.isModeratorOrSuperAdmin() &&
                    currentState.venueStatus == VenueStatus.DRAFT
                ) {
                    val editMode = currentState.mode as VenueFormMode.Edit
                    saveModeratorVenueDraft(venueId = editMode.venueId)
                }
            }
        }
    }

    private fun createVenueFromState(submitForModeration: Boolean) {
        val buildResult = currentState.toCreateVenueRequest(submitForModeration = submitForModeration)
        if (buildResult is VenueCreateRequestBuildResult.Error) {
            handleError(buildResult.message)
            return
        }
        val request = (buildResult as VenueCreateRequestBuildResult.Success).request
        createVenue(request)
    }

    private fun submitVenueDraft(submitForModeration: Boolean) {
        val editMode = currentState.mode as? VenueFormMode.Edit ?: return
        if (currentState.currentUserRole != UserRole.BUSINESS_OWNER) return

        val patchResult = currentState.toPatchVenueRequest(submitForModeration = submitForModeration)
        if (patchResult is VenuePatchRequestBuildResult.Error) {
            handleError(patchResult.message)
            return
        }

        val patchRequest = (patchResult as VenuePatchRequestBuildResult.Success).request

        launchNetwork(
            onLoading = { loading -> updateState { it.copy(isSubmitting = loading) } },
            request = {
                patchVenueDraftUseCase.execute(
                    PatchVenueDraftCommand(
                        venueId = editMode.venueId,
                        request = patchRequest
                    )
                )
            },
            onSuccess = { draft ->
                if (submitForModeration) {
                    alertController.show(
                        title = UiText.StringResource(Res.string.venue_success_update_title),
                        message = UiText.StringResource(Res.string.venue_success_update_message),
                        type = NotificationType.SUCCESS
                    )
                }
                val baseVenue = loadedVenueResponse
                if (baseVenue != null) {
                    val merged = baseVenue.applyDraftPayload(draft.payload)
                    loadedVenueResponse = merged
                    val labels = lastScheduleLabels ?: defaultScheduleLabels()
                    val currentMode = currentState.mode
                    val flaggedFieldPaths = draft.aiFindings.map { finding ->
                        normalizeAiFieldPath(finding.fieldPath)
                    }.toSet()
                    if (currentMode is VenueFormMode.Edit) {
                        updateState { state ->
                            val mappedState = merged.toVenueFormState(
                                currentState = state,
                                mode = currentMode,
                                labels = labels
                            )
                            mappedState.copy(
                                draftState = draft.state,
                                aiFindings = draft.aiFindings,
                                aiFlaggedFieldPaths = flaggedFieldPaths,
                                aiServiceErrorIds = extractServiceErrorIds(draft.aiFindings),
                                aiGalleryErrorIds = extractGalleryErrorIds(draft.aiFindings, mappedState.galleryPhotos)
                            )
                        }
                    }
                }
            },
            onError = { _, message ->
                val uiMessage = message?.let { UiText.DynamicString(it) }
                    ?: UiText.StringResource(Res.string.venue_error_update_failed)
                handleError(uiMessage)
            }
        )
    }

    private fun submitVenueForModerator(venueId: String) {
        val updateRequestResult = currentState.toUpdateVenueRequest()
        if (updateRequestResult is VenueUpdateRequestBuildResult.Error) {
            handleError(updateRequestResult.message)
            return
        }
        val updateRequest = (updateRequestResult as VenueUpdateRequestBuildResult.Success).request

        launchNetwork(
            onLoading = { loading -> updateState { it.copy(isSubmitting = loading) } },
            request = { reviewVenueDraftUseCase.execute(ReviewVenueDraftCommand(venueId = venueId, approved = true)) },
            onSuccess = {
                alertController.show(
                    title = UiText.StringResource(Res.string.venue_success_update_title),
                    message = UiText.StringResource(Res.string.venue_success_update_message),
                    type = NotificationType.SUCCESS
                )
                val mode = currentState.mode as? VenueFormMode.Edit ?: return@launchNetwork
                loadVenue(venueId = venueId, mode = mode)
            },
            onError = { code, message ->
                if (code == AppErrorCode.NOT_FOUND) {
                    performModeratorDirectSubmit(
                        venueId = venueId,
                        request = updateRequest,
                        targetStatus = resolveModeratorDirectSubmitTargetStatus()
                    )
                } else {
                    val uiMessage = message?.let { UiText.DynamicString(it) }
                        ?: UiText.StringResource(Res.string.venue_error_update_failed)
                    handleError(uiMessage)
                }
            }
        )
    }

    private fun rejectSubmittedVenueForModerator() {
        if (!currentState.currentUserRole.isModeratorOrSuperAdmin()) return
        if (currentState.draftState != com.patronat.top.dto.VenueDraftState.APPLICATION) return

        val editMode = currentState.mode as? VenueFormMode.Edit ?: return
        launchNetwork(
            onLoading = { loading -> updateState { it.copy(isSubmitting = loading) } },
            request = {
                reviewVenueDraftUseCase.execute(
                    ReviewVenueDraftCommand(
                        venueId = editMode.venueId,
                        approved = false
                    )
                )
            },
            onSuccess = {
                alertController.show(
                    title = UiText.StringResource(Res.string.venue_success_update_title),
                    message = UiText.StringResource(Res.string.venue_success_update_message),
                    type = NotificationType.SUCCESS
                )
                loadVenue(venueId = editMode.venueId, mode = editMode)
            },
            onError = { _, message ->
                val uiMessage = message?.let { UiText.DynamicString(it) }
                    ?: UiText.StringResource(Res.string.venue_error_update_failed)
                handleError(uiMessage)
            }
        )
    }

    private fun saveModeratorVenueDraft(venueId: String) {
        val updateRequestResult = currentState.toUpdateVenueRequest()
        if (updateRequestResult is VenueUpdateRequestBuildResult.Error) {
            handleError(updateRequestResult.message)
            return
        }

        val updateRequest = (updateRequestResult as VenueUpdateRequestBuildResult.Success).request
        performUpdateVenue(
            venueId = venueId,
            request = updateRequest,
            approveAfterUpdate = false,
            targetStatusAfterUpdate = VenueStatus.DRAFT
        )
    }

    private fun createVenue(request: CreateVenueRequest) {
        launchNetwork(
            onLoading = { loading -> updateState { it.copy(isSubmitting = loading) } },
            request = { createVenueUseCase.execute(CreateVenueCommand(request)) },
            onSuccess = {
                alertController.show(
                    title = UiText.StringResource(Res.string.venue_success_create_title),
                    message = UiText.StringResource(Res.string.venue_success_create_message),
                    type = NotificationType.SUCCESS
                )
                sendEffect(VenueFormEffect.NavigateBack)
            },
            onError = { _, message ->
                val uiMessage = message?.let { UiText.DynamicString(it) }
                    ?: UiText.StringResource(Res.string.venue_error_create_failed)
                handleError(uiMessage)
            }
        )
    }

    private fun loadVenue(
        venueId: String,
        mode: VenueFormMode.Edit
    ) {
        launchNetwork(
            onLoading = { loading -> updateState { it.copy(isLoading = loading) } },
            request = { getVenueByIdUseCase.execute(GetVenueByIdCommand(venueId)) },
            onSuccess = { venue ->
                loadedVenueResponse = venue
                val currentLabels = lastScheduleLabels ?: defaultScheduleLabels()
                updateState { state ->
                    venue.toVenueFormState(
                        currentState = state,
                        mode = mode,
                        labels = currentLabels
                    ).copy(
                        aiFindings = emptyList(),
                        aiFlaggedFieldPaths = emptySet(),
                        aiServiceErrorIds = emptySet(),
                        aiGalleryErrorIds = emptySet()
                    )
                }
                maybeLoadOwnerInfo(venue.ownerId)
                if (currentState.currentUserRole == UserRole.BUSINESS_OWNER) {
                    loadVenueDraft(venueId)
                } else if (currentState.currentUserRole.isModeratorOrSuperAdmin()) {
                    loadSubmittedVenueDraft(venueId)
                }
            },
            onError = { _, message ->
                val uiMessage = message?.let { UiText.DynamicString(it) }
                    ?: UiText.StringResource(Res.string.venue_error_load_failed)
                handleError(uiMessage)
            }
        )
    }

    private fun loadVenueDraft(venueId: String) {
        launchNetwork(
            request = { getVenueDraftUseCase.execute(GetVenueDraftCommand(venueId)) },
            onSuccess = { draft ->
                val baseVenue = loadedVenueResponse ?: return@launchNetwork
                val merged = baseVenue.applyDraftPayload(draft.payload)
                loadedVenueResponse = merged
                val labels = lastScheduleLabels ?: defaultScheduleLabels()
                val currentMode = currentState.mode
                val flaggedFieldPaths = draft.aiFindings.map { finding ->
                    normalizeAiFieldPath(finding.fieldPath)
                }.toSet()
                if (currentMode is VenueFormMode.Edit) {
                    updateState { state ->
                        val mappedState = merged.toVenueFormState(
                            currentState = state,
                            mode = currentMode,
                            labels = labels
                        )
                        mappedState.copy(
                            draftState = draft.state,
                            aiFindings = draft.aiFindings,
                            aiFlaggedFieldPaths = flaggedFieldPaths,
                            aiServiceErrorIds = extractServiceErrorIds(draft.aiFindings),
                            aiGalleryErrorIds = extractGalleryErrorIds(draft.aiFindings, mappedState.galleryPhotos)
                        )
                    }
                }
            },
            onError = { _, _ ->
                updateState {
                    it.copy(
                        draftState = null,
                        aiFindings = emptyList(),
                        aiFlaggedFieldPaths = emptySet(),
                        aiServiceErrorIds = emptySet(),
                        aiGalleryErrorIds = emptySet()
                    )
                }
            }
        )
    }

    private fun loadSubmittedVenueDraft(venueId: String) {
        launchNetwork(
            request = { getSubmittedVenueDraftUseCase.execute(GetSubmittedVenueDraftCommand(venueId)) },
            onSuccess = { draft ->
                val baseVenue = loadedVenueResponse ?: return@launchNetwork
                val mergedPayload = baseVenue.applyDraftPayload(draft.payload)
                val merged = draft.requestedOwnerId?.let { requestedOwnerId ->
                    mergedPayload.copy(ownerId = requestedOwnerId)
                } ?: mergedPayload
                loadedVenueResponse = merged
                val labels = lastScheduleLabels ?: defaultScheduleLabels()
                val currentMode = currentState.mode
                val flaggedFieldPaths = draft.aiFindings.map { finding ->
                    normalizeAiFieldPath(finding.fieldPath)
                }.toSet()
                if (currentMode is VenueFormMode.Edit) {
                    updateState { state ->
                        val mappedState = merged.toVenueFormState(
                            currentState = state,
                            mode = currentMode,
                            labels = labels
                        )
                        mappedState.copy(
                            draftState = draft.state,
                            aiFindings = draft.aiFindings,
                            aiFlaggedFieldPaths = flaggedFieldPaths,
                            aiServiceErrorIds = extractServiceErrorIds(draft.aiFindings),
                            aiGalleryErrorIds = extractGalleryErrorIds(draft.aiFindings, mappedState.galleryPhotos)
                        )
                    }
                    maybeLoadOwnerInfo(merged.ownerId)
                }
            },
            onError = { _, _ ->
                updateState {
                    it.copy(
                        draftState = null,
                        aiFindings = emptyList(),
                        aiFlaggedFieldPaths = emptySet(),
                        aiServiceErrorIds = emptySet(),
                        aiGalleryErrorIds = emptySet()
                    )
                }
            }
        )
    }

    private fun toggleModeratorMenu() {
        val editMode = currentState.mode as? VenueFormMode.Edit ?: return
        if (!currentState.currentUserRole.isModeratorOrSuperAdmin()) return
        if (currentState.isModeratorAssigneesLoading || currentState.isModeratorReassigning) return

        if (currentState.moderatorAssignees.isEmpty()) {
            loadModeratorAssignees()
        }

        updateState { state ->
            if (state.mode != editMode) {
                state
            } else {
                state.copy(isModeratorMenuExpanded = !state.isModeratorMenuExpanded)
            }
        }
    }

    private fun loadModeratorAssignees() {
        if (currentState.mode !is VenueFormMode.Edit || !currentState.currentUserRole.isModeratorOrSuperAdmin()) {
            return
        }
        if (currentState.moderatorAssignees.isNotEmpty()) return
        if (currentState.isModeratorAssigneesLoading) return

        launchNetwork(
            onLoading = { loading ->
                updateState { state ->
                    state.copy(isModeratorAssigneesLoading = loading)
                }
            },
            request = { getVenueAssigneesUseCase.execute(GetVenueAssigneesCommand) },
            onSuccess = { response ->
                val sortedAssignees = response.items.sortedWith(
                    compareBy(
                        { item -> item.lastName.lowercase() },
                        { item -> item.firstName.lowercase() }
                    )
                )
                updateState { state ->
                    state.copy(moderatorAssignees = sortedAssignees)
                }
            },
            onError = { _, message ->
                val uiMessage = message?.let { UiText.DynamicString(it) }
                    ?: UiText.StringResource(Res.string.venue_error_load_assignees_failed)
                handleError(uiMessage)
            }
        )
    }

    private fun reassignModerator(moderatorId: String) {
        val editMode = currentState.mode as? VenueFormMode.Edit ?: return
        if (!currentState.currentUserRole.isModeratorOrSuperAdmin()) return

        if (moderatorId == currentState.venueModeratorId) {
            updateState { it.copy(isModeratorMenuExpanded = false) }
            return
        }

        updateState { it.copy(isModeratorMenuExpanded = false) }
        launchNetwork(
            onLoading = { loading -> updateState { it.copy(isModeratorReassigning = loading) } },
            request = {
                reassignVenueModeratorUseCase.execute(
                    ReassignVenueModeratorCommand(
                        venueId = editMode.venueId,
                        moderatorId = moderatorId
                    )
                )
            },
            onSuccess = { updatedVenue ->
                applyVenueResponseToState(updatedVenue)
            },
            onError = { _, message ->
                val uiMessage = message?.let { UiText.DynamicString(it) }
                    ?: UiText.StringResource(Res.string.venue_error_reassign_failed)
                handleError(uiMessage)
            }
        )
    }

    private fun uploadGalleryFile(file: VenuePickedFile) {
        val mimeType = file.mimeType.ifBlank { "image/jpeg" }
        if (!mimeType.startsWith("image/")) {
            handleError(UiText.StringResource(Res.string.venue_error_media_upload_failed))
            return
        }

        launchNetwork(
            onLoading = { loading -> updateState { it.copy(isGalleryUploading = loading) } },
            request = {
                uploadImageUseCase.execute(
                    UploadImageCommand(
                        file = file.fileSource,
                        type = ImageType.BUSINESS,
                        mimeType = mimeType
                    )
                )
            },
            onSuccess = { registration ->
                val newPhoto = com.patronat.top.ui.models.VenueGalleryPhoto(
                    id = registration.mediaId,
                    imageUrl = registration.publicUrl
                )
                updateState { state ->
                    val exists = state.galleryPhotos.any { it.id == registration.mediaId }
                    if (exists) state else state.copy(galleryPhotos = state.galleryPhotos + newPhoto)
                }
            },
            onError = { _, message ->
                val uiMessage = message?.let { UiText.DynamicString(it) }
                    ?: UiText.StringResource(Res.string.venue_error_media_upload_failed)
                handleError(uiMessage)
            }
        )
    }

    private fun uploadServicesPdf(file: VenuePickedFile) {
        val fileName = file.fileName
        val looksLikePdf = file.mimeType.equals("application/pdf", ignoreCase = true) ||
            fileName.lowercase().endsWith(".pdf")
        if (!looksLikePdf) {
            handleError(UiText.StringResource(Res.string.venue_error_pdf_only))
            return
        }

        launchNetwork(
            onLoading = { loading -> updateState { it.copy(isServicesFileUploading = loading) } },
            request = {
                uploadImageUseCase.execute(
                    UploadImageCommand(
                        file = file.fileSource,
                        type = ImageType.BUSINESS,
                        mimeType = "application/pdf"
                    )
                )
            },
            onSuccess = { registration ->
                updateState {
                    it.copy(
                        attachedServicesFileName = fileName,
                        servicesPriceListMediaId = registration.mediaId,
                        servicesPriceListUrl = registration.publicUrl
                    )
                }
            },
            onError = { _, message ->
                val uiMessage = message?.let { UiText.DynamicString(it) }
                    ?: UiText.StringResource(Res.string.venue_error_media_upload_failed)
                handleError(uiMessage)
            }
        )
    }

    private fun updateVenue(
        venueId: String,
        request: VenueUpdateRequestBuildResult,
        approveAfterUpdate: Boolean,
        targetStatusAfterUpdate: VenueStatus? = null
    ) {
        if (request is VenueUpdateRequestBuildResult.Error) {
            handleError(request.message)
            return
        }

        val updateRequest = (request as VenueUpdateRequestBuildResult.Success).request
        performUpdateVenue(venueId, updateRequest, approveAfterUpdate, targetStatusAfterUpdate)
    }

    private fun performUpdateVenue(
        venueId: String,
        request: UpdateVenueRequest,
        approveAfterUpdate: Boolean,
        targetStatusAfterUpdate: VenueStatus? = null
    ) {
        launchNetwork(
            onLoading = { loading -> updateState { it.copy(isSubmitting = loading) } },
            request = { updateVenueUseCase.execute(UpdateVenueCommand(venueId, request)) },
            onSuccess = { response ->
                if (approveAfterUpdate) {
                    approveVenueAfterUpdate(venueId = venueId, fallbackResponse = response)
                } else if (targetStatusAfterUpdate != null) {
                    setVenueStatusAfterUpdate(
                        venueId = venueId,
                        fallbackResponse = response,
                        targetStatus = targetStatusAfterUpdate
                    )
                } else {
                    handleVenueUpdatedSuccessfully(response)
                }
            },
            onError = { _, message ->
                val uiMessage = message?.let { UiText.DynamicString(it) }
                    ?: UiText.StringResource(Res.string.venue_error_update_failed)
                handleError(uiMessage)
            }
        )
    }

    private fun performModeratorDirectSubmit(
        venueId: String,
        request: UpdateVenueRequest,
        targetStatus: VenueStatus
    ) {
        launchNetwork(
            onLoading = { loading -> updateState { it.copy(isSubmitting = loading) } },
            request = { updateVenueUseCase.execute(UpdateVenueCommand(venueId, request)) },
            onSuccess = { updatedVenue ->
                setVenueStatusAfterUpdate(
                    venueId = venueId,
                    fallbackResponse = updatedVenue,
                    targetStatus = targetStatus
                )
            },
            onError = { _, message ->
                val uiMessage = message?.let { UiText.DynamicString(it) }
                    ?: UiText.StringResource(Res.string.venue_error_update_failed)
                handleError(uiMessage)
            }
        )
    }

    private fun approveVenueAfterUpdate(
        venueId: String,
        fallbackResponse: com.patronat.top.dto.VenueResponse
    ) {
        launchNetwork(
            onLoading = { loading -> updateState { it.copy(isSubmitting = loading) } },
            request = { reviewVenueDraftUseCase.execute(ReviewVenueDraftCommand(venueId = venueId, approved = true)) },
            onSuccess = {
                setVenueStatusAfterUpdate(
                    venueId = venueId,
                    fallbackResponse = fallbackResponse,
                    targetStatus = VenueStatus.CONNECTED
                )
            },
            onError = { code, message ->
                if (code == AppErrorCode.NOT_FOUND) {
                    setVenueStatusAfterUpdate(
                        venueId = venueId,
                        fallbackResponse = fallbackResponse,
                        targetStatus = VenueStatus.CONNECTED
                    )
                } else {
                    applyVenueResponseToState(fallbackResponse)
                    val uiMessage = message?.let { UiText.DynamicString(it) }
                        ?: UiText.StringResource(Res.string.venue_error_update_failed)
                    handleError(uiMessage)
                }
            }
        )
    }

    private fun setVenueStatusAfterUpdate(
        venueId: String,
        fallbackResponse: com.patronat.top.dto.VenueResponse,
        targetStatus: VenueStatus
    ) {
        launchNetwork(
            onLoading = { loading -> updateState { it.copy(isSubmitting = loading) } },
            request = {
                updateVenueStatusUseCase.execute(
                    UpdateVenueStatusCommand(
                        venueId = venueId,
                        status = targetStatus
                    )
                )
            },
            onSuccess = { approvedResponse ->
                handleVenueUpdatedSuccessfully(approvedResponse)
            },
            onError = { _, message ->
                applyVenueResponseToState(fallbackResponse)
                val uiMessage = message?.let { UiText.DynamicString(it) }
                    ?: UiText.StringResource(Res.string.venue_error_update_failed)
                handleError(uiMessage)
            }
        )
    }

    private fun resolveModeratorDirectSubmitTargetStatus(): VenueStatus {
        return when (currentState.venueStatus) {
            VenueStatus.DRAFT,
            VenueStatus.ADDED_BY_MODERATOR -> VenueStatus.ADDED_BY_MODERATOR
            else -> VenueStatus.CONNECTED
        }
    }

    private fun handleVenueUpdatedSuccessfully(response: com.patronat.top.dto.VenueResponse) {
        applyVenueResponseToState(response)
        alertController.show(
            title = UiText.StringResource(Res.string.venue_success_update_title),
            message = UiText.StringResource(Res.string.venue_success_update_message),
            type = NotificationType.SUCCESS
        )
    }

    private fun applyVenueResponseToState(response: com.patronat.top.dto.VenueResponse) {
        loadedVenueResponse = response
        val labels = lastScheduleLabels ?: defaultScheduleLabels()
        val currentMode = currentState.mode
        if (currentMode is VenueFormMode.Edit) {
            updateState { state ->
                response.toVenueFormState(
                    currentState = state,
                    mode = currentMode,
                    labels = labels
                ).copy(
                    aiFindings = emptyList(),
                    aiFlaggedFieldPaths = emptySet(),
                    aiServiceErrorIds = emptySet(),
                    aiGalleryErrorIds = emptySet()
                )
            }
            maybeLoadOwnerInfo(response.ownerId)
        }
    }

    private fun maybeLoadOwnerInfo(ownerId: String?) {
        if (
            !currentState.currentUserRole.isModeratorOrSuperAdmin() ||
            ownerId.isNullOrBlank() ||
            ownerId == UNASSIGNED_VENUE_OWNER_ID
        ) {
            updateState { state -> state.copy(ownerInfo = OwnerInfoData()) }
            return
        }

        loadOwnerInfo(ownerId)
    }

    private fun loadOwnerInfo(ownerId: String) {
        launchNetwork(
            request = { getUserByIdUseCase.execute(GetUserByIdCommand(ownerId)) },
            onSuccess = { user ->
                updateState { state ->
                    if (state.ownerId != ownerId) {
                        state
                    } else {
                        state.copy(
                            ownerInfo = OwnerInfoData(
                                firstName = user.firstName,
                                lastName = user.lastName,
                                phone = user.phone,
                                email = user.email.orEmpty()
                            ),
                            ownerReadOnly = true
                        )
                    }
                }
            },
            onError = { _, _ ->
                updateState { state ->
                    if (state.ownerId == ownerId) {
                        state.copy(ownerInfo = OwnerInfoData())
                    } else {
                        state
                    }
                }
            }
        )
    }

    private fun defaultScheduleLabels(): VenueScheduleDayLabels {
        return VenueScheduleDayLabels(
            monday = "Понеділок",
            tuesday = "Вівторок",
            wednesday = "Середа",
            thursday = "Четвер",
            friday = "Пʼятниця",
            saturday = "Субота",
            sunday = "Неділя"
        )
    }

    private fun handleError(message: UiText) {
        alertController.show(
            title = UiText.StringResource(DesignRes.string.error_alert_title),
            message = message,
            type = NotificationType.ERROR
        )
    }

    private fun addService() {
        updateState { state ->
            val newItem = VenueServiceItemData(id = "service_${state.nextServiceIndex}")
            state.copy(
                services = state.services + newItem,
                nextServiceIndex = state.nextServiceIndex + 1
            )
        }
    }

    private fun buildDefaultSchedule(labels: VenueScheduleDayLabels): List<WorkScheduleDay> {
        return listOf(
            WorkScheduleDay(id = "mon", label = labels.monday, isOpen = true, openTime = "09:00", closeTime = "18:00"),
            WorkScheduleDay(id = "tue", label = labels.tuesday, isOpen = true, openTime = "09:00", closeTime = "18:00"),
            WorkScheduleDay(id = "wed", label = labels.wednesday, isOpen = true, openTime = "09:00", closeTime = "18:00"),
            WorkScheduleDay(id = "thu", label = labels.thursday, isOpen = true, openTime = "09:00", closeTime = "18:00"),
            WorkScheduleDay(id = "fri", label = labels.friday, isOpen = true, openTime = "09:00", closeTime = "18:00"),
            WorkScheduleDay(id = "sat", label = labels.saturday, isOpen = false, openTime = "", closeTime = ""),
            WorkScheduleDay(id = "sun", label = labels.sunday, isOpen = false, openTime = "", closeTime = "")
        )
    }

    private fun relabelScheduleDays(
        days: List<WorkScheduleDay>,
        labels: VenueScheduleDayLabels
    ): List<WorkScheduleDay> {
        if (days.isEmpty()) return buildDefaultSchedule(labels)

        return days.map { day ->
            val newLabel = when (day.id.lowercase()) {
                "mon" -> labels.monday
                "tue" -> labels.tuesday
                "wed" -> labels.wednesday
                "thu" -> labels.thursday
                "fri" -> labels.friday
                "sat" -> labels.saturday
                "sun" -> labels.sunday
                else -> day.label
            }
            day.copy(label = newLabel)
        }
    }

    private fun extractServiceErrorIds(findings: List<VenueAiFinding>): Set<String> {
        return findings.mapNotNull { finding ->
            val normalizedPath = normalizeAiFieldPath(finding.fieldPath)
            val match = Regex("""^services\[(\d+)\]\.""").find(normalizedPath) ?: return@mapNotNull null
            val index = match.groupValues.getOrNull(1)?.toIntOrNull() ?: return@mapNotNull null
            "service_${index + 1}"
        }.toSet()
    }

    private fun extractGalleryErrorIds(
        findings: List<VenueAiFinding>,
        galleryPhotos: List<VenueGalleryPhoto>
    ): Set<String> {
        if (findings.isEmpty() || galleryPhotos.isEmpty()) return emptySet()

        val allGalleryFlagged = findings.any { finding ->
            val path = normalizeAiFieldPath(finding.fieldPath)
            path == "gallery" || path.startsWith("gallery.")
        }
        if (allGalleryFlagged) {
            return galleryPhotos.map { it.id }.toSet()
        }

        return findings.mapNotNull { finding ->
            val normalizedPath = normalizeAiFieldPath(finding.fieldPath)
            val indexedMatch = Regex("""^gallery\[(\d+)\]""").find(normalizedPath)
                ?: Regex("""^gallery\.(\d+)""").find(normalizedPath)
                ?: return@mapNotNull null
            val index = indexedMatch.groupValues.getOrNull(1)?.toIntOrNull() ?: return@mapNotNull null
            galleryPhotos.getOrNull(index)?.id
        }.toSet()
    }

    private fun normalizeAiFieldPath(raw: String): String {
        val value = raw.trim()
            .removePrefix("$.")
            .removePrefix("payload.")
            .removePrefix("venue.")
        return when (value.lowercase()) {
            "name" -> "main.name"
            "description" -> "main.description"
            "phone" -> "main.phone"
            "website" -> "main.website"
            "email" -> "main.email"
            "category" -> "main.category"
            "address", "location", "location.address" -> "location.address"
            "schedule", "workschedule", "work_schedule" -> "schedule"
            "gallery", "images", "photos" -> "gallery"
            else -> {
                Regex("""^services\.(\d+)\.(name|description)$""", RegexOption.IGNORE_CASE)
                    .matchEntire(value)
                    ?.let { match ->
                        "services[${match.groupValues[1]}].${match.groupValues[2].lowercase()}"
                    }
                    ?: Regex("""^gallery\.(\d+)$""", RegexOption.IGNORE_CASE)
                        .matchEntire(value)
                        ?.let { match -> "gallery[${match.groupValues[1]}]" }
                    ?: value
            }
        }
    }
}

private fun UserRole?.isModeratorOrSuperAdmin(): Boolean {
    return this == UserRole.MODERATOR || this == UserRole.SUPER_ADMIN
}
